import type { InteractionEvent } from '../../interaction/types';
import type { IViewport } from '../../viewports/types';
import type { ITool } from '../types';
import { FrameCoalescer } from './frame-coalescer';

/**
 * Zoom sensitivity: world units of zoom factor per normalized drag unit.
 * Cornerstone uses 5/canvasHeight; we expose a similar factor.
 */
const DRAG_ZOOM_FACTOR = 5;

/** Simulated drag pixels per wheel click (matches Cornerstone's synthetic drag). */
const WHEEL_DELTA_PX = 5;

/**
 * Last-resort floor for orthographic zoom, used only by viewports that expose
 * no parallelScale range of their own. Keeps the camera out of a degenerate
 * scale; it is not a usability limit.
 */
const MIN_PARALLEL_SCALE = 0.01;

/**
 * Camera zoom tool — adjusts camera zoom level via drag or scroll wheel.
 *
 * Follows Cornerstone3D ZoomTool approach:
 * - Orthographic: adjusts parallelScale with normalized factor k = deltaY * (5 / canvasHeight)
 * - Perspective: dollies camera along direction-of-projection scaled by sqrt(distance)
 *
 * Direction: dragging up zooms in, dragging down zooms out. Scrolling down
 * zooms in (one wheel click is a synthetic 5 px upward drag), scrolling up
 * zooms out.
 *
 * Zoom factor is normalized by canvas height so behavior is resolution-independent.
 */
export class ZoomTool implements ITool {
    static readonly toolName = 'Zoom';

    readonly id = ZoomTool.toolName;
    readonly name = 'Zoom';
    readonly cursor = 'zoom-in';
    readonly completed = false;

    private pendingDeltaY = 0;
    private pendingViewport: IViewport | null = null;
    private coalescer = new FrameCoalescer(() => this.applyPending());

    activate(_viewport: IViewport): void {}

    deactivate(): void {
        this.coalescer.cancel();
        this.resetPending();
    }

    cancel(): void {
        this.coalescer.cancel();
        this.resetPending();
    }

    handleEvent(event: InteractionEvent): boolean {
        switch (event.type) {
            case 'mouse_drag_end': {
                this.coalescer.flush();
                return true;
            }

            case 'mouse_drag': {
                this.pendingDeltaY += event.deltaPoints.canvas[1];
                this.pendingViewport = event.viewport;
                this.coalescer.schedule();
                return true;
            }

            case 'wheel': {
                if (event.deltaY === undefined) return false;
                const direction = event.deltaY > 0 ? 1 : -1;
                this.pendingDeltaY += -direction * WHEEL_DELTA_PX;
                this.pendingViewport = event.viewport;
                this.coalescer.schedule();
                return true;
            }

            default:
                return false;
        }
    }

    private applyPending(): void {
        const vp = this.pendingViewport;
        const deltaY = this.pendingDeltaY;
        this.resetPending();

        if (!vp || deltaY === 0) return;
        this.zoom(vp, deltaY);
        vp.render();
    }

    private resetPending(): void {
        this.pendingDeltaY = 0;
        this.pendingViewport = null;
    }

    private zoom(viewport: IViewport, deltaY: number): void {
        const renderer = viewport.getRenderer(0);
        const camera = renderer.getActiveCamera();
        const canvas = viewport.getCanvas();
        if (!canvas) return;

        const canvasHeight = canvas.getBoundingClientRect().height;
        if (canvasHeight === 0) return;

        // Normalized zoom factor — resolution independent.
        // Negated against the canvas Y axis (which grows downwards) so that
        // dragging up zooms in and dragging down zooms out.
        const k = -deltaY * (DRAG_ZOOM_FACTOR / canvasHeight);
        if (camera.getParallelProjection()) {
            // Orthographic: adjust parallelScale
            // Drag up (negative deltaY) → positive k → (1-k) < 1 → smaller scale → zoom in
            const scale = (1 - k) * camera.getParallelScale();
            // Viewports that know the physical extent of what they display
            // bound the zoom; the others only get the degenerate-scale floor.
            const range = viewport.getParallelScaleRange();
            const newScale = range
                ? Math.min(Math.max(scale, range.min), range.max)
                : Math.max(MIN_PARALLEL_SCALE, scale);
            camera.setParallelScale(newScale);
        } else {
            // Perspective: adjust view angle to preserve focal point (rotation center).
            // Clamp to [1°, 179°] to prevent the projection flipping upside-down.
            // Drag up (negative deltaY) → positive k → (1+k) > 1 → smaller angle → zoom in
            const newAngle = camera.getViewAngle() / (1 + k);
            camera.setViewAngle(Math.max(1, Math.min(179, newAngle)));
        }

        viewport.updateCameraClippingRange();
    }
}
